import { FC, useCallback, useEffect, useRef, useState } from "react";
import EmbeddedLayout from "../../components/admin/EmbeddedLayout";
import LoadingAnimation from "../../components/_common/Loading";
import InvoicesController from "../../Api/invoicesController";
import { useToast } from "../../hooks/useToast";
import { IProforma } from "../../interfaces/IProforma";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    Box,
    Dialog,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tab,
    Tabs,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InvoiceItem from "./InvoiceItem/InvoiceItem";
import AddInvoiceButton from "./components/AddInvoiceButton";

interface InvoicesPageProps { embedded?: boolean }

const InvoicesPage: FC<InvoicesPageProps> = ({ embedded = false }) => {
    const [nameValue, setNameValue] = useState<string>('');
    const [showSetName, setShowSetName] = useState(false);
    const [invoicesData, setInvoicesData] = useState<IProforma[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState<number>(Number(localStorage.getItem('invoiceLimit')) || 20);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const toast = useRef(useToast()).current
    const [tableValue, setTableValue] = useState(Number(localStorage.getItem('invoiceTable')) || 0);

    const handleChangeTable = (event: React.SyntheticEvent, newValue: number) => {
        setTableValue(newValue);
        localStorage.setItem('invoiceTable', String(newValue));
        setPage(1);
    };


    const handleSetName = (name: string) => {
        localStorage.setItem('invoiceName', name);
        setNameValue('')
    }


    const getInvoices = useCallback(async (pageNumber = 1, pageLimit = limit) => {
        setLoading(true);
        let statusValue: IProforma['status'] | undefined = undefined;
        if (tableValue === 0) statusValue = 'awaiting_adv'
        if (tableValue === 1) statusValue = 'adv_paid'
        if (tableValue === 2) statusValue = 'sent'
        if (tableValue === 3) statusValue = 'received'
        if (tableValue === 4) statusValue = undefined
        if (tableValue === 5) statusValue = 'cancelled'
        const res = await InvoicesController.getInvoicesData({ page: pageNumber, limit: pageLimit, statusValue });
        if (!res.ok) {
            setLoading(false);
            return toast.error(res.message);
        }
        setInvoicesData(res.invoiceData);
        setTotalPages(res.totalPages || 1);
        setLoading(false);
    }, [toast, limit, tableValue]);


    useEffect(() => {
        getInvoices(page, limit);
    }, [getInvoices, page, limit, tableValue]);


    if (loading) return (
        <EmbeddedLayout embedded={embedded} title="Wczytywanie">
            <LoadingAnimation loadingText="Wczytywanie...." />
        </EmbeddedLayout>
    )


    return (
        <EmbeddedLayout embedded={embedded} title="Faktury">
            <AddInvoiceButton />
            <Typography variant="h4" fontWeight={700} mb={3} textAlign="center">
                Lista faktur proforma
            </Typography>
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex' }}>
                    Zalogowano jako:&nbsp;
                    <Box
                        component="span"
                        fontWeight={600}
                        color="primary.main"
                        onClick={() => setShowSetName(true)}
                    >
                        {localStorage.getItem('invoiceName') ?? 'Błąd'}
                    </Box>
                    <Box
                        onClick={() => setShowSetName(true)}
                        color={'secondary.main'}
                        sx={{ ml: 0.5, cursor: 'pointer', textDecoration: 'underline' }}
                        fontSize='small'
                    >
                        zmień
                    </Box>
                </Typography>
            </Box>
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} mt={-2}>
                <Typography sx={{ mr: 2 }}>Wyświetl na stronie:</Typography>
                <Select
                    value={String(limit)}
                    onChange={e => {
                        setLimit(Number(e.target.value));
                        localStorage.setItem('invoiceLimit', e.target.value);
                        setPage(1);
                    }}
                    size="small"
                    sx={{ width: 90, background: "#fff" }}
                    MenuProps={{ disableScrollLock: true }}
                >
                    {[5, 10, 20, 50, 100].map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                </Select>
            </Box>
            <Dialog open={showSetName} onClose={() => setShowSetName(false)}>
                <DialogTitle>Zmień podpis na fakturze</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Wystawił"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={nameValue}
                        onChange={e => setNameValue(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSetName(false)}>Anuluj</Button>
                    <Button onClick={() => {
                        handleSetName(nameValue);
                        setShowSetName(false);
                    }}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>
            <Box
                mb={1}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Tabs
                    value={tableValue}
                    onChange={handleChangeTable}
                    aria-label="basic tabs example"
                    centered
                >
                    <Tab value={0} label="Oczekujące na zaliczkę" />
                    <Tab value={1} label="Do wysłania" />
                    <Tab value={2} label="Wysłane do realizacji" />
                    <Tab value={3} label="Zrealizowane" />
                    <Tab value={4} label="Wszystkie" />
                    <Tab value={5} label="Anulowane" />
                </Tabs>
            </Box>
            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nr</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Klient</TableCell>
                            <TableCell>Kwota</TableCell>
                            <TableCell>Zaliczka</TableCell>
                            <TableCell sx={{ textAlign: 'center', pl: 5 }}>Status</TableCell>
                            <TableCell sx={{ pl: 5 }}>Zk</TableCell>
                            <TableCell sx={{ pl: 5 }}>Zd</TableCell>
                            <TableCell>Akcje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoicesData && invoicesData.length > 0 ? (
                            invoicesData.map((invoice) => (
                                <InvoiceItem
                                    key={invoice._id}
                                    setInvoicesData={setInvoicesData}
                                    invoice={invoice}
                                />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Brak faktur do wyświetlenia.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={4} mb={2}>
                <Stack spacing={2}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                    />
                </Stack>
            </Box>
        </EmbeddedLayout>
    )
}

export default InvoicesPage;
